import React, { useEffect } from 'react';
import Head from 'next/head';
import Container from '../../components/container';
import PostBody from '../../components/post-body';
import PostHeader from '../../components/post-header';
import SectionSeparator from '../../components/section-separator';
import Layout from '../../components/layout';
import Tags from '../../components/tags';
import AlsoRead from '../../components/also-read';
import NavBar from '../../components/navbar';
import MoreStoriesList from '../../components/more-stories-list';
import SocialIconShare from '../../components/social-icon-share';
import config from '../../lib/config';

import { GetStaticPaths, GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import { getAllPostsWithSlug, getPostAndMorePosts, getPrimaryMenuItems } from '../../lib/api';
import { H3 } from '../../components/text';
import { IMenuItem } from '../../models/menu-item';
import { ISponsor } from '../../models/sponsor';
import { ITeam } from '../../models/team';
import { IPost, IPostDirect } from '../../models/post';

interface IProps {
	primaryMenuItems: IMenuItem[];
	post: IPostDirect;
	posts: { edges: IPost[] };
	sponsors: ISponsor[];
	teams: ITeam[];
	preview?: boolean;
}

export default function Post({ primaryMenuItems, post, posts, sponsors, preview }: IProps) {
	const router = useRouter();

	useEffect(() => {
		if (post.videoLinks?.youtubeLink) {
			router.replace(`/video/${post.slug}`);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [post]);

	if (post.videoLinks?.youtubeLink) {
		return null;
	}

	const morePosts = posts?.edges;
	const pageTitle = `${post.title} | ${config.siteName}`;
	const meta = post.seo?.description || post.title;

	return (
		<Layout preview={preview} sponsors={sponsors} metaTitle={pageTitle} metaDesc={meta}>
			<article>
				<Head>
					<title>{pageTitle}</title>
					{post.featuredImage && <meta property="og:image" content={post.featuredImage.node.sourceUrl} />}
				</Head>
				<NavBar items={primaryMenuItems} />
				{post.featuredImage && (
					<div className="relative h-auto lg:h-[540px] w-full">
						<img
							src={post.featuredImage.node.sourceUrl}
							alt={post.title}
							className="object-cover object-center aspect-video-16/9 w-full h-full"
						/>
					</div>
				)}
				<Container className="lg:py-12 xl:py-16">
					<div className="flex flex-col lg:flex-row">
						<div className="w-full lg:w-4/12 xl:w-3/12 mb-12 h-auto">
							{/* <div className="bg-white flex justify-center items-center w-full h-[300px] mb-6 lg:mb-12">
								<Label className="text-slate-400">Advertentieruimte</Label>
							</div> */}
							<div className="my-6 hidden lg:block">
								<H3 className="mb-4">Verder lezen</H3>
								{morePosts.length > 0 && <MoreStoriesList posts={morePosts} snug />}
							</div>
						</div>
						<div className="flex-1 lg:pl-6 xl:pl-16 w-full lg:w-8/12 xl:w-9/12">
							<PostHeader title={post.title} date={post.date} categories={post.categories} />
							{post.content && <PostBody content={post.content} />}
							<footer>
								{post.tags?.edges && post.tags.edges.length > 0 && <Tags tags={post.tags} />}
							</footer>
							<SocialIconShare post={post} />
							<SectionSeparator />
							<div className="block lg:hidden">
								<H3 className="mb-6">Verder lezen</H3>
								{morePosts.length > 0 && <AlsoRead posts={morePosts} />}
							</div>
						</div>
					</div>
				</Container>
			</article>
		</Layout>
	);
}

export const getStaticProps: GetStaticProps = async ({ params, preview = false, previewData }) => {
	const { posts, post, sponsors } = await getPostAndMorePosts(params?.slug as string, preview, previewData);

	if (!post) {
		return {
			notFound: true,
		};
	}

	const primaryMenuItems = await getPrimaryMenuItems();

	return {
		revalidate: 10,
		props: {
			sponsors: sponsors.nodes,
			preview,
			post,
			posts,
			primaryMenuItems,
		},
	};
};

export const getStaticPaths: GetStaticPaths = async () => {
	const allPosts = await getAllPostsWithSlug();
	const mappedPaths = allPosts.posts.edges.map(({ node }) => `/nieuws/${node.slug}`);

	return {
		paths: mappedPaths,
		fallback: 'blocking',
	};
};
